// Tutorials Module - Zustand Store
// Manages tutorial pages, editor state, and AI panel state
import { create } from 'zustand';
import { tutorialAPI } from '@/lib/api';
import { toast } from 'sonner';

// Détail d'erreur backend, y compris quand la réponse est un Blob (responseType: 'blob')
async function readErrorDetail(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || '';
  if (typeof data === 'string') return data.slice(0, 200);
  if (data.detail) return data.detail;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      try { return JSON.parse(text).detail || text.slice(0, 200); } catch { return text.slice(0, 200); }
    } catch { return ''; }
  }
  return '';
}

const useTutorialStore = create((set, get) => ({
  // Pages state
  pages: [],
  currentPage: null,
  loading: false,
  error: null,

  // Editor state
  saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  isFullscreen: false,
  lastSavedAt: null,

  // AI panel state
  aiPanelOpen: false,
  aiLoading: false,
  aiResult: null,
  aiHistory: [],

  // Search & filter
  searchQuery: '',
  filterStatus: null, // null = all, 'draft', 'published'
  filterCategory: 'all', // 'all' or category string

  // Tree state
  expandedNodes: {},

  // ==================== PAGES ACTIONS ====================

  fetchPages: async (userId, params = {}) => {
    set({ loading: true, error: null });
    try {
      const res = await tutorialAPI.list({ user_id: userId, ...params });
      set({ pages: res.data, loading: false });
    } catch (error) {
      set({ error: error.message, loading: false });
      toast.error('Erreur lors du chargement des tutoriels');
    }
  },

  fetchPage: async (pageId, userId) => {
    set({ loading: true, error: null });
    try {
      const res = await tutorialAPI.get(pageId, userId);
      set({ currentPage: res.data, loading: false });
      return res.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      toast.error('Erreur lors du chargement de la page');
      return null;
    }
  },

  createPage: async (data, userId) => {
    try {
      const res = await tutorialAPI.create(data, userId);
      set(state => ({
        pages: [...state.pages, res.data],
        currentPage: res.data,
      }));
      toast.success('Page créée');
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      return null;
    }
  },

  updatePage: async (pageId, data, userId, saveVersion = false) => {
    set({ saveStatus: 'saving' });
    try {
      const res = await tutorialAPI.update(pageId, data, userId, saveVersion);
      set(state => ({
        currentPage: res.data,
        pages: state.pages.map(p => p.id === pageId ? { ...p, ...res.data } : p),
        saveStatus: 'saved',
        lastSavedAt: new Date(),
      }));
      // Reset saved status after 3s
      setTimeout(() => {
        const current = get().saveStatus;
        if (current === 'saved') set({ saveStatus: 'idle' });
      }, 3000);
      return res.data;
    } catch (error) {
      set({ saveStatus: 'error' });
      return null;
    }
  },

  deletePage: async (pageId, userId) => {
    try {
      await tutorialAPI.delete(pageId, userId);
      set(state => ({
        pages: state.pages.filter(p => p.id !== pageId && p.parent_id !== pageId),
        currentPage: state.currentPage?.id === pageId ? null : state.currentPage,
      }));
      toast.success('Page supprimée');
      return true;
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      return false;
    }
  },

  duplicatePage: async (pageId, userId) => {
    try {
      const res = await tutorialAPI.duplicate(pageId, userId);
      set(state => ({ pages: [...state.pages, res.data] }));
      toast.success('Page dupliquée');
      return res.data;
    } catch (error) {
      toast.error('Erreur lors de la duplication');
      return null;
    }
  },

  toggleStatus: async (pageId, userId) => {
    try {
      const res = await tutorialAPI.toggleStatus(pageId, userId);
      const newStatus = res.data.status;
      const childrenUpdated = res.data.children_updated || 0;
      set(state => {
        let updatedPages = state.pages.map(p =>
          p.id === pageId ? { ...p, status: newStatus } : p
        );
        // If published and children were cascaded, update them too
        if (newStatus === 'published' && childrenUpdated > 0) {
          const findDescendants = (pid) => {
            const children = updatedPages.filter(p => p.parent_id === pid);
            let ids = children.map(c => c.id);
            children.forEach(c => { ids = ids.concat(findDescendants(c.id)); });
            return ids;
          };
          const descendantIds = findDescendants(pageId);
          updatedPages = updatedPages.map(p =>
            descendantIds.includes(p.id)
              ? { ...p, status: 'published', is_visible: true }
              : p
          );
        }
        return {
          pages: updatedPages,
          currentPage: state.currentPage?.id === pageId
            ? { ...state.currentPage, status: newStatus }
            : state.currentPage,
        };
      });
      const msg = newStatus === 'published'
        ? `Page publiée${childrenUpdated > 0 ? ` (+ ${childrenUpdated} sous-pages)` : ''}`
        : 'Page repassée en brouillon';
      toast.success(msg);
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  },

  toggleVisibility: async (pageId, userId) => {
    try {
      const res = await tutorialAPI.toggleVisibility(pageId, userId);
      set(state => ({
        pages: state.pages.map(p => p.id === pageId ? { ...p, is_visible: res.data.is_visible } : p),
        currentPage: state.currentPage?.id === pageId
          ? { ...state.currentPage, is_visible: res.data.is_visible }
          : state.currentPage,
      }));
      toast.success(res.data.is_visible ? 'Visible pour les candidats' : 'Masqué pour les candidats');
    } catch (error) {
      toast.error('Erreur lors du changement de visibilité');
    }
  },

  reorderPages: async (items, userId) => {
    try {
      await tutorialAPI.reorder(items, userId);
      // Update local state immediately
      set(state => {
        const updatedPages = [...state.pages];
        for (const item of items) {
          const idx = updatedPages.findIndex(p => p.id === item.id);
          if (idx !== -1) {
            updatedPages[idx] = { ...updatedPages[idx], parent_id: item.parent_id, order: item.order };
          }
        }
        return { pages: updatedPages };
      });
    } catch (error) {
      toast.error('Erreur lors de la réorganisation');
    }
  },

  // ==================== EDITOR ACTIONS ====================

  setCurrentPage: (page) => set({ currentPage: page }),
  setSaveStatus: (status) => set({ saveStatus: status }),
  setFullscreen: (val) => set({ isFullscreen: val }),

  // ==================== SEARCH & FILTER ====================

  setSearchQuery: (query) => set({ searchQuery: query }),
  setFilterStatus: (status) => set({ filterStatus: status }),
  setFilterCategory: (category) => set({ filterCategory: category }),

  // ==================== TREE STATE ====================

  toggleNode: (nodeId) => set(state => ({
    expandedNodes: {
      ...state.expandedNodes,
      [nodeId]: !state.expandedNodes[nodeId],
    },
  })),

  expandAll: () => set(state => {
    const expanded = {};
    state.pages.forEach(p => { expanded[p.id] = true; });
    return { expandedNodes: expanded };
  }),

  // Déplie une branche précise (chaîne d'ancêtres de la page ouverte)
  expandNodes: (ids) => set(state => {
    const expanded = { ...state.expandedNodes };
    (ids || []).forEach(id => { if (id) expanded[id] = true; });
    return { expandedNodes: expanded };
  }),

  collapseAll: () => set({ expandedNodes: {} }),

  // ==================== AI PANEL ====================

  toggleAIPanel: () => set(state => ({ aiPanelOpen: !state.aiPanelOpen })),
  setAIPanelOpen: (open) => set({ aiPanelOpen: open }),

  aiGenerate: async (data, userId) => {
    set({ aiLoading: true, aiResult: null });
    try {
      const res = await tutorialAPI.aiGenerate(data, userId);
      set(state => ({
        aiLoading: false,
        aiResult: res.data,
        aiHistory: [
          { id: Date.now(), type: 'generate', prompt: data.prompt, result: res.data, timestamp: new Date() },
          ...state.aiHistory,
        ],
      }));
      return res.data;
    } catch (error) {
      set({ aiLoading: false });
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération IA');
      return null;
    }
  },

  aiImprove: async (data, userId) => {
    set({ aiLoading: true, aiResult: null });
    try {
      const res = await tutorialAPI.aiImprove(data, userId);
      set(state => ({
        aiLoading: false,
        aiResult: res.data,
        aiHistory: [
          { id: Date.now(), type: 'improve', action: data.action, result: res.data, timestamp: new Date() },
          ...state.aiHistory,
        ],
      }));
      return res.data;
    } catch (error) {
      set({ aiLoading: false });
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'amélioration IA');
      return null;
    }
  },

  aiCodeExample: async (data, userId) => {
    set({ aiLoading: true, aiResult: null });
    try {
      const res = await tutorialAPI.aiCodeExample(data, userId);
      set(state => ({
        aiLoading: false,
        aiResult: res.data,
        aiHistory: [
          { id: Date.now(), type: 'code', topic: data.topic, result: res.data, timestamp: new Date() },
          ...state.aiHistory,
        ],
      }));
      return res.data;
    } catch (error) {
      set({ aiLoading: false });
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération de code');
      return null;
    }
  },

  clearAIResult: () => set({ aiResult: null }),

  // ==================== EXPORT ====================

  exportHTML: async (pageId, userId) => {
    try {
      const res = await tutorialAPI.exportHTML(pageId, userId);
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const page = get().pages.find(p => p.id === pageId) || get().currentPage;
      a.href = url;
      a.download = `${page?.title || 'tutoriel'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export HTML téléchargé');
    } catch (error) {
      // La réponse est un blob : le détail d'erreur du backend doit être lu comme texte
      const detail = await readErrorDetail(error);
      console.error('[export HTML]', error);
      toast.error(detail ? `Export HTML : ${detail}` : 'Erreur lors de l\'export HTML');
    }
  },

  exportPDF: async (pageId, userId) => {
    try {
      const res = await tutorialAPI.exportPDF(pageId, userId);
      // Le backend renvoie désormais un document HTML complet (blob)
      const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        toast.error('Fenêtre bloquée — autorisez les pop-ups pour exporter en PDF');
        URL.revokeObjectURL(url);
        return;
      }

      // Déclencher l'impression une fois la page chargée
      let printed = false;
      const launchPrint = () => {
        if (printed || printWindow.closed) return;
        printed = true;
        printWindow.focus();
        printWindow.print();
      };
      printWindow.onload = launchPrint;
      setTimeout(launchPrint, 800);

      // Libérer le blob après un délai raisonnable
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      toast.success('Export PDF ouvert — enregistrez en PDF depuis la boîte d\'impression');
    } catch (error) {
      const detail = await readErrorDetail(error);
      console.error('[export PDF]', error);
      toast.error(detail ? `Export PDF : ${detail}` : 'Erreur lors de l\'export PDF');
    }
  },
}));

export default useTutorialStore;
